import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChalkboardTeacher,
  faCalendarPlus,
  faCircleCheck,
  faClock,
  faVideo,
  faCommentDots,
  faCertificate,
  faTrophy,
  faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './DemoMyClasses.css';

const API = process.env.REACT_APP_API_BASE_URL;
const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;
const waUrl = (text) => `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const AFTER = [
  { icon: faVideo, title: 'A Join button for every class', text: 'Same teacher, same days and times every week.' },
  { icon: faCommentDots, title: 'Feedback after each class', text: 'What your child learned and built, written by the teacher.' },
  { icon: faCertificate, title: 'Projects and certificates', text: 'A certificate at the end of every course.' },
  { icon: faTrophy, title: 'Challenges, medals and badges', text: 'A new challenge every month with the other students.' },
];

/* /DemoClass/myclasses: what "My Classes" looks like before enrolling
   (the free class, an empty weekly schedule to fill, and what comes after enrolling) */
const DemoMyClasses = () => {
  const navigate = useNavigate();
  const studentId = sessionStorage.getItem('demoStudentId');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!studentId) {
      navigate('/joinfreeclass', { replace: true });
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`${API}/api/demo/details/${studentId}`)
      .then((res) => !cancelled && setData(res.data))
      .catch(() => !cancelled && setData({}));
    return () => {
      cancelled = true;
    };
  }, [studentId, navigate]);

  const firstName = (data?.student?.fullName || '').split(' ')[0] || 'your child';
  const session = data?.schedule?.[0] || null;
  const start = session?.date ? new Date(session.date) : null;
  const valid = start && !Number.isNaN(start.getTime());
  const done = valid && start.getTime() + 60 * 60000 < Date.now();
  const teacher = session?.teacher || '';
  const course = data?.student?.interestedCourse || '';

  return (
    <div className="rmdmc">
      <Link to="/DemoClass" className="rmdmc-back">
        <FontAwesomeIcon icon={faArrowLeft} /> Dashboard
      </Link>
      <header className="rmdmc-head">
        <span className="rmdmc-eyebrow">
          <FontAwesomeIcon icon={faChalkboardTeacher} /> My Classes
        </span>
        <h1>Your classes</h1>
        <p>This is where {firstName}’s weekly classes will be once you enroll.</p>
      </header>

      {/* free class */}
      <section className="rmdmc-card">
        <h2>Free class</h2>
        {!data ? (
          <div className="rmdmc-skel" />
        ) : valid ? (
          <div className="rmdmc-free">
            <span className={`rmdmc-pill ${done ? 'is-done' : ''}`}>
              <FontAwesomeIcon icon={done ? faCircleCheck : faClock} /> {done ? 'Done' : 'Coming up'}
            </span>
            <div>
              <b>{start.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</b>
              <span>
                {' '}
                · {start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                {teacher ? ` · with ${teacher}` : ''}
                {course ? ` · ${course}` : ''}
              </span>
            </div>
          </div>
        ) : (
          <p className="rmdmc-dim">Your free class isn’t booked yet.</p>
        )}
      </section>

      {/* empty weekly schedule */}
      <section className="rmdmc-card">
        <h2>Weekly classes</h2>
        <div className="rmdmc-week">
          <ul aria-hidden="true">
            {DAYS.map((d) => (
              <li key={d}>
                <b>{d}</b>
                <span />
                <span />
              </li>
            ))}
          </ul>
          <div className="rmdmc-empty">
            <span className="rmdmc-empty-icon">
              <FontAwesomeIcon icon={faCalendarPlus} />
            </span>
            <h3>No weekly classes yet</h3>
            <p>Pick 1 or 2 times a week{teacher ? ` with ${teacher}` : ''}, live 1-to-1 on Zoom, at times that suit you.</p>
            <a
              className="rmdmc-btn"
              href={waUrl(`Hi! I'd like to enroll ${firstName}${teacher ? ` with ${teacher}` : ''}. Which days and times are free?`)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> Choose our weekly times
            </a>
          </div>
        </div>
      </section>

      {/* what comes after enrolling */}
      <section className="rmdmc-card">
        <h2>After enrolling, you’ll see here</h2>
        <ul className="rmdmc-after">
          {AFTER.map((a) => (
            <li key={a.title}>
              <span>
                <FontAwesomeIcon icon={a.icon} />
              </span>
              <div>
                <b>{a.title}</b>
                <p>{a.text}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default DemoMyClasses;
